import React from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Heart,
  Shield,
  Brain,
  CheckCircle,
  ArrowRight,
  Phone,
  Calendar,
  Award,
  Users,
  BookOpen,
  MapPin,
  Clock,
  Zap,
  Target
} from 'lucide-react';

const Radiotherapy: React.FC = () => {
  return (
    <>
      <Helmet>
        <title>Radiotherapy Anxiety Support | Sharon Clare Hypnotherapy Belfast & Northern Ireland</title>
        <meta
          name="description"
          content="Helping you find Calm, Confidence, and Control during radiotherapy treatment. Specialised hypnotherapy for radiotherapy anxiety, mask intolerance, and cancer treatment support in Belfast and Northern Ireland."
        />
        <meta name="keywords" content="radiotherapy anxiety, cancer treatment support, hypnotherapy Belfast, mask intolerance, brachytherapy anxiety, radiotherapy mask anxiety, head and neck cancer, Belfast City Hospital, Macmillan support" />
        <meta property="og:title" content="Radiotherapy Anxiety Support | Sharon Clare Hypnotherapy" />
        <meta property="og:description" content="Helping you find Calm, Confidence, and Control during radiotherapy treatment. Specialised hypnotherapy for radiotherapy anxiety and mask intolerance." />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Radiotherapy Anxiety Support | Sharon Clare Hypnotherapy" />
        <meta name="twitter:description" content="Helping you find Calm, Confidence, and Control during radiotherapy treatment. Specialised hypnotherapy for radiotherapy anxiety and mask intolerance." />
        <link rel="canonical" href="https://www.sharonclare-hypnotherapy.com/radiotherapy-anxiety/" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "What if I panicked during my mask fitting — is it too late to get help?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Not at all. Many people I work with first seek support after a difficult mask-fitting experience. Even after one session, most people feel more able to tolerate the mask and complete treatment comfortably."
                }
              },
              {
                "@type": "Question",
                "name": "Do I need all three sessions?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Not necessarily. Session 1 provides the essential tools most people need. Sessions 2 and 3 deepen your confidence and help you feel steady throughout and beyond treatment. You can choose the level of support that feels right for you."
                }
              },
              {
                "@type": "Question",
                "name": "Will I need to talk about past trauma or difficult memories?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "No. Solution Focused Hypnotherapy is gentle and future-focused. You do not need to revisit painful experiences for this work to be effective."
                }
              },
              {
                "@type": "Question",
                "name": "What if I usually cope well — why did this happen to me?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Radiotherapy anxiety is extremely common, especially for people who are normally calm, strong, and capable. It is a normal protective response from the part of the brain that handles survival. Once you understand why it happened, you can regain control quickly."
                }
              },
              {
                "@type": "Question",
                "name": "Can I use these tools during treatment?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes. Everything you learn can be used discreetly: inside the mask, in the waiting room, during scans, when side-effects feel overwhelming, or anytime anxiety rises. These tools are designed for real moments of pressure."
                }
              },
              {
                "@type": "Question",
                "name": "Is hypnotherapy safe during radiotherapy?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes. Clinical Solution Focused Hypnotherapy is gentle, non-invasive, and focused on calming the nervous system. It works alongside your medical treatment and does not interfere with any care you are receiving."
                }
              },
              {
                "@type": "Question",
                "name": "Can hypnotherapy help with anxiety around other types of radiotherapy, not just head-and-neck treatment?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes. Although many people come to me because of the immobilisation mask used in head and neck radiotherapy, the tools I teach are effective for any radiotherapy treatment where anxiety, anticipation or a sense of vulnerability is present."
                }
              },
              {
                "@type": "Question",
                "name": "Can this help with brachytherapy (internal radiotherapy)?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, absolutely. Brachytherapy can feel invasive and unfamiliar. The self-hypnosis tools, breath regulation and Anchor technique can help you stay calmer before, during, and after the procedure."
                }
              },
              {
                "@type": "Question",
                "name": "Can hypnotherapy help with the anxiety of attending radiotherapy every day for weeks?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes. The tools you learn help with anticipatory anxiety before appointments, daily emotional wear-and-tear, managing worry spirals about side-effects, and staying steady throughout the treatment schedule."
                }
              }
            ]
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "serviceType": "Radiotherapy Anxiety Support Hypnotherapy",
            "name": "Radiotherapy Anxiety Support",
            "description": "Specialized hypnotherapy to help cancer patients find calm, confidence, and control during radiotherapy treatment. Supporting mask intolerance, treatment anxiety, and procedural stress in Belfast and Northern Ireland.",
            "provider": {
              "@type": "MedicalBusiness",
              "name": "Sharon Clare Hypnotherapy",
              "telephone": "07932139245",
              "email": "Sharon@sharonclare-hypnotherapy.com",
              "url": "https://www.sharonclare-hypnotherapy.com/",
              "address": [
                {
                  "@type": "PostalAddress",
                  "name": "Belfast Wellness Clinic",
                  "streetAddress": "Belfast Wellness Clinic, 228 Ormeau Road",
                  "addressLocality": "Belfast",
                  "addressRegion": "County Antrim",
                  "postalCode": "BT7 2FZ",
                  "addressCountry": "GB"
                },
                {
                  "@type": "PostalAddress",
                  "name": "The Arzen Collective",
                  "streetAddress": "The Arzen Collective, 13B Antrim Street",
                  "addressLocality": "Lisburn",
                  "addressRegion": "County Antrim",
                  "postalCode": "BT28",
                  "addressCountry": "GB"
                }
              ]
            },
            "areaServed": ["Belfast", "Lisburn", "Northern Ireland", "UK"],
            "availableChannel": {
              "@type": "ServiceChannel",
              "serviceUrl": "https://www.sharonclare-hypnotherapy.com/radiotherapy-anxiety",
              "servicePhone": {
                "@type": "ContactPoint",
                "telephone": "07932139245",
                "contactType": "customer service"
              }
            }
          })}
        </script>
      </Helmet>

      <main>
        {/* Hero Section */}
        <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
          <div className="container-custom">
            <div className="text-center max-w-4xl mx-auto">
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-gray-900 mb-6">
                Radiotherapy Anxiety Support
              </h1>
              <p className="text-xl sm:text-2xl text-gray-600 mb-6 leading-relaxed font-semibold">
                Helping you find Calm, Confidence, and Control during treatment.
              </p>
              <p className="text-lg sm:text-xl text-gray-600 mb-8 leading-relaxed">
                If you've felt frightened, overwhelmed, or unable to tolerate the radiotherapy mask, you are not alone.
              </p>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                Even people who are calm in every other part of their lives can panic when the mask is fitted.
              </p>
              <div className="bg-white rounded-2xl p-6 mb-8 shadow-lg border-2 border-accent-200 max-w-2xl mx-auto">
                <p className="text-lg text-gray-900 font-semibold mb-2">Here's the most important thing to know:</p>
                <p className="text-gray-700 mb-2">Your reaction is normal.</p>
                <p className="text-gray-700 mb-2">Your brain is protecting you.</p>
                <p className="text-gray-700">And you can learn to feel calmer and more in control — quickly and safely.</p>
              </div>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="/book"
                  className="btn-primary text-lg px-8 py-4 inline-flex items-center justify-center"
                >
                  <Calendar className="w-5 h-5 mr-2" />
                  Book Your Appointment
                </a>
                <a
                  href="tel:07932139245"
                  className="btn-secondary text-lg px-8 py-4 inline-flex items-center justify-center"
                >
                  <Phone className="w-5 h-5 mr-2" />
                  Call 07932 139245
                </a>
              </div>
              <div className="flex items-center gap-2 text-sm text-slate-600 justify-center mt-4">
                <MapPin className="h-4 w-4 text-teal-600 flex-shrink-0" />
                <span>Volunteers at the Radiotherapy Department, Belfast City Hospital</span>
              </div>
            </div>
          </div>
        </section>

        {/* Why Radiotherapy Can Trigger Panic */}
        <section className="py-16 sm:py-20 bg-white">
          <div className="container-custom">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6">
                Why Radiotherapy Can Trigger Panic
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed mb-8">
                Radiotherapy for head and neck cancer involves a tight immobilisation mask. It is essential for accuracy, but it can feel restrictive and unfamiliar. That can trigger:
              </p>
              <div className="grid md:grid-cols-2 gap-6 mb-8">
                <div className="flex items-start space-x-3">
                  <Heart className="w-6 h-6 text-accent-600 flex-shrink-0 mt-1" />
                  <span className="text-gray-700">Claustrophobia</span>
                </div>
                <div className="flex items-start space-x-3">
                  <Heart className="w-6 h-6 text-accent-600 flex-shrink-0 mt-1" />
                  <span className="text-gray-700">Breathlessness</span>
                </div>
                <div className="flex items-start space-x-3">
                  <Heart className="w-6 h-6 text-accent-600 flex-shrink-0 mt-1" />
                  <span className="text-gray-700">A rush of heat or panic</span>
                </div>
                <div className="flex items-start space-x-3">
                  <Heart className="w-6 h-6 text-accent-600 flex-shrink-0 mt-1" />
                  <span className="text-gray-700">Flashbacks or body memories</span>
                </div>
                <div className="flex items-start space-x-3">
                  <Heart className="w-6 h-6 text-accent-600 flex-shrink-0 mt-1" />
                  <span className="text-gray-700">A sense of losing control</span>
                </div>
              </div>
              <p className="text-lg text-gray-600 leading-relaxed mb-4">
                This has nothing to do with "strength" and everything to do with how the primitive brain keeps you safe.
              </p>
              <p className="text-lg text-gray-700 font-semibold">
                Once you understand what is happening in the brain, you can change how your body responds — often much quicker than people expect.
              </p>
            </div>
          </div>
        </section>

        {/* Why Solution Focused Hypnotherapy Works So Well */}
        <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
          <div className="container-custom">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6">
                Why Solution Focused Hypnotherapy Works So Well
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed mb-8">
                Solution Focused Hypnotherapy (SFH) is uniquely suited to radiotherapy anxiety because it is:
              </p>
              <div className="grid md:grid-cols-2 gap-8">
                <div className="bg-white rounded-2xl p-8 shadow-lg">
                  <div className="flex items-center mb-4">
                    <Zap className="w-8 h-8 text-accent-600 mr-3" />
                    <h3 className="text-xl font-heading font-semibold text-gray-900">Rapid</h3>
                  </div>
                  <p className="text-gray-600">Most people feel a significant shift after just one session.</p>
                </div>
                <div className="bg-white rounded-2xl p-8 shadow-lg">
                  <div className="flex items-center mb-4">
                    <Shield className="w-8 h-8 text-accent-600 mr-3" />
                    <h3 className="text-xl font-heading font-semibold text-gray-900">Non-intrusive</h3>
                  </div>
                  <p className="text-gray-600">You do not need to retell your story or revisit difficult memories.</p>
                </div>
                <div className="bg-white rounded-2xl p-8 shadow-lg">
                  <div className="flex items-center mb-4">
                    <Brain className="w-8 h-8 text-accent-600 mr-3" />
                    <h3 className="text-xl font-heading font-semibold text-gray-900">Based on neuroscience</h3>
                  </div>
                  <p className="text-gray-600">You learn why your brain reacted that way — and how to switch on the part of the brain that restores Calm, Confidence, and Control.</p>
                </div>
                <div className="bg-white rounded-2xl p-8 shadow-lg">
                  <div className="flex items-center mb-4">
                    <Target className="w-8 h-8 text-accent-600 mr-3" />
                    <h3 className="text-xl font-heading font-semibold text-gray-900">Practical</h3>
                  </div>
                  <p className="text-gray-600">You leave with tools you can use instantly inside the mask, during treatment, in waiting rooms, and at home.</p>
                </div>
                <div className="bg-white rounded-2xl p-8 shadow-lg md:col-span-2">
                  <div className="flex items-center mb-4">
                    <Heart className="w-8 h-8 text-accent-600 mr-3" />
                    <h3 className="text-xl font-heading font-semibold text-gray-900">Complementary to medical care</h3>
                  </div>
                  <p className="text-gray-600">You learn techniques your radiotherapy team can gently prompt you to use during treatment.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* What We Do in Sessions */}
        <section className="py-16 sm:py-20 bg-white">
          <div className="container-custom">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6">
                What We Do in Sessions
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed mb-8">
                Across one, two, or three sessions, you learn a proven set of tools that work together to regulate your nervous system:
              </p>

              <div className="space-y-6 mb-8">
                <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-6">
                  <div className="flex items-start space-x-4">
                    <div className="w-8 h-8 bg-accent-600 text-white rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <span className="text-sm font-bold">1</span>
                    </div>
                    <div>
                      <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">Personalised Self-Hypnosis (Safe Place)</h3>
                      <p className="text-gray-700 mb-3">
                        A location or memory where you feel calm, confident, and in control — kayaking on still water, a coastal walk, a favourite family place…
                      </p>
                      <p className="text-gray-700 font-semibold">
                        This becomes a powerful internal refuge during treatment.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-6">
                  <div className="flex items-start space-x-4">
                    <div className="w-8 h-8 bg-accent-600 text-white rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <span className="text-sm font-bold">2</span>
                    </div>
                    <div>
                      <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">The Anchor</h3>
                      <p className="text-gray-700 mb-3">
                        A discreet physical cue that can stop panic rising.
                      </p>
                      <p className="text-gray-700 font-semibold">
                        Patients often describe it as "emptying the stress bucket instantly."
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-6">
                  <div className="flex items-start space-x-4">
                    <div className="w-8 h-8 bg-accent-600 text-white rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <span className="text-sm font-bold">3</span>
                    </div>
                    <div>
                      <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">Breath Regulation</h3>
                      <p className="text-gray-700">
                        Simple techniques to lower adrenaline and bring the body back into safety.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-6">
                  <div className="flex items-start space-x-4">
                    <div className="w-8 h-8 bg-accent-600 text-white rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <span className="text-sm font-bold">4</span>
                    </div>
                    <div>
                      <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">Positive Visualisation</h3>
                      <p className="text-gray-700">
                        Training the brain to rehearse calm instead of fear.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-6">
                  <div className="flex items-start space-x-4">
                    <div className="w-8 h-8 bg-accent-600 text-white rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <span className="text-sm font-bold">5</span>
                    </div>
                    <div>
                      <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">The Rewind Technique (when needed)</h3>
                      <p className="text-gray-700">
                        Gently resolves older fear memories so they no longer intrude during treatment.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl p-8 shadow-lg border-2 border-accent-200 text-center">
                <p className="text-lg text-gray-700 mb-2">Together, these tools give you something essential:</p>
                <p className="text-xl font-semibold text-gray-900">
                  a way to influence your experience — even when treatment itself cannot change.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* My Experience */}
        <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
          <div className="container-custom">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6">
                My Experience
              </h2>
              <h3 className="text-xl sm:text-2xl font-heading font-semibold text-gray-900 mb-6">
                Trusted by Radiotherapy Teams — Available to Everyone
              </h3>
              <div className="bg-white rounded-2xl p-8 shadow-lg mb-6">
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  In addition to my private practice, I volunteer with the Macmillan Information & Support Centre and the Radiotherapy Department at Belfast City Hospital, supporting patients who struggle with radiotherapy anxiety or mask intolerance.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  This pilot programme is designed to support people who might otherwise:
                </p>
                <ul className="space-y-3 mb-6">
                  <li className="flex items-start space-x-3">
                    <CheckCircle className="w-6 h-6 text-accent-600 flex-shrink-0 mt-1" />
                    <span className="text-gray-700">delay treatment</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <CheckCircle className="w-6 h-6 text-accent-600 flex-shrink-0 mt-1" />
                    <span className="text-gray-700">refuse treatment</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <CheckCircle className="w-6 h-6 text-accent-600 flex-shrink-0 mt-1" />
                    <span className="text-gray-700">opt for more invasive alternatives</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <CheckCircle className="w-6 h-6 text-accent-600 flex-shrink-0 mt-1" />
                    <span className="text-gray-700">rely solely on diazepam because no rapid emotional support is available</span>
                  </li>
                </ul>
                <p className="text-lg text-gray-700 leading-relaxed">
                  Although early, outcomes have been extremely positive — and this experience now informs the support I offer to anyone undergoing radiotherapy, whether referred through the NHS or seeking private help.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Who This Support Helps */}
        <section className="py-16 sm:py-20 bg-white">
          <div className="container-custom">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6">
                Who This Support Helps
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed mb-6">
                This approach is ideal if you:
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="flex items-start space-x-3">
                  <CheckCircle className="w-6 h-6 text-accent-600 flex-shrink-0 mt-1" />
                  <span className="text-gray-700">panicked during mask fitting</span>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="w-6 h-6 text-accent-600 flex-shrink-0 mt-1" />
                  <span className="text-gray-700">managed only seconds or minutes inside the mask</span>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="w-6 h-6 text-accent-600 flex-shrink-0 mt-1" />
                  <span className="text-gray-700">are dreading upcoming treatment</span>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="w-6 h-6 text-accent-600 flex-shrink-0 mt-1" />
                  <span className="text-gray-700">want tools to feel calm, confident, and in control</span>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="w-6 h-6 text-accent-600 flex-shrink-0 mt-1" />
                  <span className="text-gray-700">prefer a gentle, non-intrusive therapeutic approach</span>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="w-6 h-6 text-accent-600 flex-shrink-0 mt-1" />
                  <span className="text-gray-700">want to complete radiotherapy safely and successfully</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* In-Person Sessions */}
        <section className="py-16 sm:py-20 bg-gradient-to-br from-accent-50 to-primary-50">
          <div className="container-custom">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8 text-center">
                In-Person Sessions
              </h2>

              <div className="bg-white rounded-2xl p-8 shadow-lg mb-8">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-4">
                  Appointments available at:
                </h3>
                <div className="space-y-4 mb-6">
                  <div className="flex items-start space-x-3">
                    <MapPin className="w-6 h-6 text-accent-600 flex-shrink-0 mt-1" />
                    <span className="text-gray-700">Belfast Wellness Clinic, 228 Ormeau Road, Belfast BT7 2FZ (days and times vary, early evening appointments available)</span>
                  </div>
                  <div className="flex items-start space-x-3">
                    <MapPin className="w-6 h-6 text-accent-600 flex-shrink-0 mt-1" />
                    <span className="text-gray-700">The Arzen Collective, 13B Antrim Street, Lisburn BT28</span>
                  </div>
                </div>

                <p className="text-gray-700 mb-8">
                  The radiotherapy support programme is offered as a three-session programme, with an
                  urgent one-off session available when treatment is imminent. Online sessions are
                  also available. You can see current options and book on the appointment page.
                </p>

                <div className="text-center">
                  <a
                    href="/book"
                    className="btn-primary text-lg px-8 py-4 inline-flex items-center justify-center"
                  >
                    <Calendar className="w-5 h-5 mr-2" />
                    Book Your Appointment
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Digital Programme */}
        <section className="py-16 sm:py-20 bg-white">
          <div className="container-custom">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-8">
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-4">
                  Digital Radiotherapy Anxiety Programme
                </h2>
                <div className="inline-block bg-accent-100 text-accent-900 px-4 py-2 rounded-full font-semibold mb-6">
                  Coming Soon
                </div>
              </div>

              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-2xl p-8 mb-8">
                <h3 className="text-xl sm:text-2xl font-heading font-semibold text-gray-900 mb-4">
                  Learn the same tools as an in-person session — from home, in your own time.
                </h3>
                <p className="text-lg text-gray-700 leading-relaxed mb-4">
                  If you're not familiar with digital programmes, here's what it means:
                </p>
                <p className="text-gray-700 leading-relaxed mb-6">
                  You'll receive a series of videos where I guide you through the same tools and techniques I teach in a face-to-face session. It's just like being in the room with me — calm, step-by-step, and easy to follow.
                </p>

                <h4 className="text-lg font-semibold text-gray-900 mb-4">Alongside the videos, you'll also receive:</h4>
                <div className="space-y-4 mb-6">
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="w-6 h-6 text-accent-600 flex-shrink-0 mt-1" />
                    <div>
                      <span className="font-semibold text-gray-900">Downloadable audio recordings</span>
                      <p className="text-gray-600 text-sm">Guided self-hypnosis and relaxation tools you can use before or during treatment.</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="w-6 h-6 text-accent-600 flex-shrink-0 mt-1" />
                    <div>
                      <span className="font-semibold text-gray-900">PDF summaries and step-by-step guides</span>
                      <p className="text-gray-600 text-sm">Simple, printable sheets you can bring to appointments or read before treatment.</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="w-6 h-6 text-accent-600 flex-shrink-0 mt-1" />
                    <div>
                      <span className="font-semibold text-gray-900">Clear explanations of the brain</span>
                      <p className="text-gray-600 text-sm">So you understand exactly why your reaction happened — and how to take back control.</p>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-xl p-6 mb-6">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">This makes the digital programme ideal for anyone who:</h4>
                  <ul className="space-y-2">
                    <li className="flex items-start space-x-2">
                      <span className="text-accent-600 font-bold">•</span>
                      <span className="text-gray-700">prefers to learn privately at home</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-accent-600 font-bold">•</span>
                      <span className="text-gray-700">wants to revisit the tools before every treatment</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-accent-600 font-bold">•</span>
                      <span className="text-gray-700">wants support immediately without waiting for an appointment</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="bg-white rounded-2xl p-8 shadow-lg mb-8">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-6 text-center">
                  You can choose exactly what you need:
                </h3>

                <div className="space-y-6">
                  <div className="border-2 border-accent-200 rounded-xl p-6">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h4 className="text-lg font-semibold text-gray-900">Session 1 — Essential Tools</h4>
                        <p className="text-sm text-gray-600">(Core Programme)</p>
                      </div>
                    </div>
                    <p className="text-gray-700 mb-2">
                      Includes the full brain explanation, personalised Safe Place self-hypnosis, breathwork, and the core regulation tools.
                    </p>
                    <p className="text-gray-900 font-semibold">
                      Recommended for everyone experiencing radiotherapy anxiety.
                    </p>
                  </div>

                  <div className="border-2 border-gray-200 rounded-xl p-6">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h4 className="text-lg font-semibold text-gray-900">Add-On Session 2 — The Anchor & Advanced Techniques</h4>
                      </div>
                    </div>
                    <p className="text-gray-700">
                      Learn the Anchor and additional in-treatment strategies.
                    </p>
                  </div>

                  <div className="border-2 border-gray-200 rounded-xl p-6">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h4 className="text-lg font-semibold text-gray-900">Add-On Session 3 — Thinking Beyond Treatment</h4>
                      </div>
                    </div>
                    <p className="text-gray-700">
                      Tools for emotional steadiness after treatment and reducing future anxiety templates.
                    </p>
                  </div>

                  <div className="border-4 border-accent-300 rounded-xl p-6 bg-gradient-to-br from-accent-50 to-primary-50">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h4 className="text-lg font-semibold text-gray-900">Full Programme — All 3 Sessions</h4>
                        <p className="text-sm text-accent-700 font-semibold">Best value</p>
                      </div>
                    </div>
                    <p className="text-gray-700">
                      Mirrors the structure used in the radiotherapy pilot.
                    </p>
                  </div>
                </div>
              </div>

              <div className="text-center">
                <p className="text-lg text-gray-700 mb-4">
                  Join the waitlist to be notified the moment it becomes available:
                </p>
                <a
                  href="https://sharonclare.ck.page/7187651"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary text-lg px-8 py-4 inline-flex items-center justify-center"
                >
                  Join Waitlist
                  <ArrowRight className="w-5 h-5 ml-2" />
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Closing Message */}
        <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
          <div className="container-custom">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6">
                You Don't Have to Face This Alone
              </h2>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Radiotherapy is one of the most important treatments you will ever receive — and you deserve to feel supported, steady, and safe throughout it.
              </p>
              <div className="bg-white rounded-2xl p-8 shadow-lg mb-8">
                <p className="text-xl text-gray-900 mb-2">You can feel calmer.</p>
                <p className="text-xl text-gray-900 mb-2">You can regain confidence.</p>
                <p className="text-xl text-gray-900 mb-6">You can complete your treatment with a sense of control.</p>
                <p className="text-lg text-gray-700 font-semibold">
                  I'm here to help you every step of the way.
                </p>
              </div>
              <div className="mb-6">
                <p className="text-lg text-gray-900 font-semibold mb-2">Sharon Clare</p>
                <p className="text-gray-700">Clinical Solution-Focused Hypnotherapist</p>
                <p className="text-gray-600 text-sm">HPD | DSFH | CNHC (Reg) | AfSFH (Reg)</p>
              </div>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="/book"
                  className="btn-primary text-lg px-8 py-4 inline-flex items-center justify-center"
                >
                  <Calendar className="w-5 h-5 mr-2" />
                  Book Your Appointment
                </a>
                <a
                  href="tel:07932139245"
                  className="btn-secondary text-lg px-8 py-4 inline-flex items-center justify-center"
                >
                  <Phone className="w-5 h-5 mr-2" />
                  Call 07932 139245
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Location Callout */}
        <section className="py-12 bg-gradient-to-r from-accent-600 to-primary-600">
          <div className="container-custom">
            <div className="max-w-4xl mx-auto text-center text-white">
              <div className="flex items-center justify-center mb-4">
                <MapPin className="w-8 h-8 mr-3" />
                <h3 className="text-2xl font-heading font-bold">Two Convenient Clinic Locations</h3>
              </div>
              <p className="text-xl mb-6 text-white/90">
                My Belfast clinic on the Ormeau Road is <strong>close to Belfast City Hospital</strong>, ideal for patients at the Northern Ireland Cancer Centre. I also see clients at <strong>The Arzen Collective in Lisburn</strong>, and online across Northern Ireland and the UK.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <a
                  href="/belfast-hypnotherapy"
                  className="bg-white text-accent-600 px-6 py-3 rounded-lg hover:bg-gray-50 transition-colors font-semibold inline-flex items-center"
                >
                  <MapPin className="w-5 h-5 mr-2" />
                  Belfast Clinic Details
                </a>
                <span className="text-white/80">Also available via <a href="/contact" className="underline hover:text-white">online sessions</a></span>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 sm:py-20 bg-white">
          <div className="container-custom">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
                Frequently Asked Questions
              </h2>

              <div className="space-y-6">
                <details className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-2xl p-6 group">
                  <summary className="font-semibold text-gray-900 cursor-pointer text-lg mb-2">
                    What if I panicked during my mask fitting — is it too late to get help?
                  </summary>
                  <p className="text-gray-700 leading-relaxed pl-4">
                    Not at all. Many people I work with first seek support after a difficult mask-fitting experience. Even after one session, most people feel more able to tolerate the mask and complete treatment comfortably.
                  </p>
                </details>

                <details className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-2xl p-6 group">
                  <summary className="font-semibold text-gray-900 cursor-pointer text-lg mb-2">
                    Do I need all three sessions?
                  </summary>
                  <p className="text-gray-700 leading-relaxed pl-4">
                    Not necessarily. Session 1 provides the essential tools most people need. Sessions 2 and 3 deepen your confidence and help you feel steady throughout and beyond treatment. You can choose the level of support that feels right for you.
                  </p>
                </details>

                <details className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-2xl p-6 group">
                  <summary className="font-semibold text-gray-900 cursor-pointer text-lg mb-2">
                    Will I need to talk about past trauma or difficult memories?
                  </summary>
                  <p className="text-gray-700 leading-relaxed pl-4">
                    No. Solution Focused Hypnotherapy is gentle and future-focused. You do not need to revisit painful experiences for this work to be effective.
                  </p>
                </details>

                <details className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-2xl p-6 group">
                  <summary className="font-semibold text-gray-900 cursor-pointer text-lg mb-2">
                    What if I usually cope well — why did this happen to me?
                  </summary>
                  <p className="text-gray-700 leading-relaxed pl-4">
                    Radiotherapy anxiety is extremely common, especially for people who are normally calm, strong, and capable. It is a normal protective response from the part of the brain that handles survival. Once you understand why it happened, you can regain control quickly.
                  </p>
                </details>

                <details className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-2xl p-6 group">
                  <summary className="font-semibold text-gray-900 cursor-pointer text-lg mb-2">
                    Can I use these tools during treatment?
                  </summary>
                  <div className="text-gray-700 leading-relaxed pl-4">
                    <p className="mb-3">Yes. Everything you learn can be used discreetly:</p>
                    <ul className="space-y-2 mb-3">
                      <li className="flex items-start space-x-2">
                        <span className="text-accent-600">•</span>
                        <span>inside the mask</span>
                      </li>
                      <li className="flex items-start space-x-2">
                        <span className="text-accent-600">•</span>
                        <span>in the waiting room</span>
                      </li>
                      <li className="flex items-start space-x-2">
                        <span className="text-accent-600">•</span>
                        <span>during scans</span>
                      </li>
                      <li className="flex items-start space-x-2">
                        <span className="text-accent-600">•</span>
                        <span>when side-effects feel overwhelming</span>
                      </li>
                      <li className="flex items-start space-x-2">
                        <span className="text-accent-600">•</span>
                        <span>or anytime anxiety rises</span>
                      </li>
                    </ul>
                    <p>These tools are designed for real moments of pressure.</p>
                  </div>
                </details>

                <details className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-2xl p-6 group">
                  <summary className="font-semibold text-gray-900 cursor-pointer text-lg mb-2">
                    Is hypnotherapy safe during radiotherapy?
                  </summary>
                  <p className="text-gray-700 leading-relaxed pl-4">
                    Yes. Clinical Solution Focused Hypnotherapy is gentle, non-invasive, and focused on calming the nervous system. It works alongside your medical treatment and does not interfere with any care you are receiving.
                  </p>
                </details>

                <details className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-2xl p-6 group">
                  <summary className="font-semibold text-gray-900 cursor-pointer text-lg mb-2">
                    Do I need to attend in person?
                  </summary>
                  <p className="text-gray-700 leading-relaxed pl-4">
                    You're welcome to. But if you prefer support from home, the digital version of this programme will be available. You'll be able to choose from one, two, or three sessions depending on the support you want.
                  </p>
                </details>

                <details className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-2xl p-6 group">
                  <summary className="font-semibold text-gray-900 cursor-pointer text-lg mb-2">
                    What if I'm already taking diazepam?
                  </summary>
                  <p className="text-gray-700 leading-relaxed pl-4">
                    Hypnotherapy can still help you. Many people receive diazepam when there are no rapid psychological services available. This programme gives you additional tools that work with your nervous system, not just medication.
                  </p>
                </details>

                <details className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-2xl p-6 group">
                  <summary className="font-semibold text-gray-900 cursor-pointer text-lg mb-2">
                    Can hypnotherapy help with anxiety around other types of radiotherapy, not just head-and-neck treatment?
                  </summary>
                  <p className="text-gray-700 leading-relaxed pl-4">
                    Yes. Although many people come to me because of the immobilisation mask used in head and neck radiotherapy, the tools I teach are effective for any radiotherapy treatment where anxiety, anticipation or a sense of vulnerability is present. The techniques we use — breathwork, self-hypnosis, the Safe Place, the Anchor, and the neuroscience explanation — help calm the autonomic nervous system in any setting.
                  </p>
                </details>

                <details className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-2xl p-6 group">
                  <summary className="font-semibold text-gray-900 cursor-pointer text-lg mb-2">
                    Can this help with brachytherapy (internal radiotherapy)?
                  </summary>
                  <div className="text-gray-700 leading-relaxed pl-4">
                    <p className="mb-3">Yes, absolutely. Brachytherapy can feel invasive and unfamiliar, and the preparation or procedure can trigger:</p>
                    <ul className="space-y-2 mb-3">
                      <li className="flex items-start space-x-2">
                        <span className="text-accent-600">•</span>
                        <span>anticipatory anxiety</span>
                      </li>
                      <li className="flex items-start space-x-2">
                        <span className="text-accent-600">•</span>
                        <span>fear of discomfort</span>
                      </li>
                      <li className="flex items-start space-x-2">
                        <span className="text-accent-600">•</span>
                        <span>worries about losing control</span>
                      </li>
                      <li className="flex items-start space-x-2">
                        <span className="text-accent-600">•</span>
                        <span>difficulty relaxing the body during the procedure</span>
                      </li>
                    </ul>
                    <p className="mb-3">The self-hypnosis tools, breath regulation and Anchor technique can help you stay calmer before, during, and after the procedure. Many people also find that understanding why the body reacts as it does reduces fear significantly.</p>
                    <p>You do not need to have a mask-related treatment for the programme to help you.</p>
                  </div>
                </details>

                <details className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-2xl p-6 group">
                  <summary className="font-semibold text-gray-900 cursor-pointer text-lg mb-2">
                    What about external beam radiotherapy to other parts of the body (e.g., pelvis, breast, abdomen)?
                  </summary>
                  <div className="text-gray-700 leading-relaxed pl-4">
                    <p className="mb-3">Yes, the programme is designed to support any radiotherapy where anxiety makes treatment more difficult.</p>
                    <p className="mb-3">Common concerns during other radiotherapy types include:</p>
                    <ul className="space-y-2 mb-3">
                      <li className="flex items-start space-x-2">
                        <span className="text-accent-600">•</span>
                        <span>fear of the machine</span>
                      </li>
                      <li className="flex items-start space-x-2">
                        <span className="text-accent-600">•</span>
                        <span>struggling to remain still</span>
                      </li>
                      <li className="flex items-start space-x-2">
                        <span className="text-accent-600">•</span>
                        <span>intrusive thoughts during treatment</span>
                      </li>
                      <li className="flex items-start space-x-2">
                        <span className="text-accent-600">•</span>
                        <span>worrying about side-effects or what the machine is doing</span>
                      </li>
                      <li className="flex items-start space-x-2">
                        <span className="text-accent-600">•</span>
                        <span>fear of feeling exposed or vulnerable</span>
                      </li>
                    </ul>
                    <p>The same tools apply beautifully to these situations and help people feel safer, calmer, and more prepared.</p>
                  </div>
                </details>

                <details className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-2xl p-6 group">
                  <summary className="font-semibold text-gray-900 cursor-pointer text-lg mb-2">
                    Can hypnotherapy help with the anxiety of attending radiotherapy every day for weeks?
                  </summary>
                  <div className="text-gray-700 leading-relaxed pl-4">
                    <p className="mb-3">Yes. Many people feel anxiety not only during treatment but also around the routine itself: travelling, anticipation, changes in appetite, sleep disruption, or fatigue.</p>
                    <p className="mb-3">The tools you learn help with:</p>
                    <ul className="space-y-2 mb-3">
                      <li className="flex items-start space-x-2">
                        <span className="text-accent-600">•</span>
                        <span>anticipatory anxiety before appointments</span>
                      </li>
                      <li className="flex items-start space-x-2">
                        <span className="text-accent-600">•</span>
                        <span>daily emotional wear-and-tear</span>
                      </li>
                      <li className="flex items-start space-x-2">
                        <span className="text-accent-600">•</span>
                        <span>managing worry spirals about side-effects</span>
                      </li>
                      <li className="flex items-start space-x-2">
                        <span className="text-accent-600">•</span>
                        <span>staying steady throughout the treatment schedule</span>
                      </li>
                    </ul>
                    <p>This is why many patients continue using the techniques long after radiotherapy ends.</p>
                  </div>
                </details>

                <details className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-2xl p-6 group">
                  <summary className="font-semibold text-gray-900 cursor-pointer text-lg mb-2">
                    Is this helpful if I'm claustrophobic but my radiotherapy doesn't involve a mask?
                  </summary>
                  <div className="text-gray-700 leading-relaxed pl-4">
                    <p className="mb-3">Yes. Claustrophobia can arise in:</p>
                    <ul className="space-y-2 mb-3">
                      <li className="flex items-start space-x-2">
                        <span className="text-accent-600">•</span>
                        <span>MRI or CT planning scans</span>
                      </li>
                      <li className="flex items-start space-x-2">
                        <span className="text-accent-600">•</span>
                        <span>treatment room positioning</span>
                      </li>
                      <li className="flex items-start space-x-2">
                        <span className="text-accent-600">•</span>
                        <span>being alone in the treatment suite</span>
                      </li>
                      <li className="flex items-start space-x-2">
                        <span className="text-accent-600">•</span>
                        <span>lying still for long periods</span>
                      </li>
                    </ul>
                    <p>SFH gives you practical tools to manage these reactions calmly and safely.</p>
                  </div>
                </details>

                <details className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-2xl p-6 group">
                  <summary className="font-semibold text-gray-900 cursor-pointer text-lg mb-2">
                    Will the digital programme work for non-mask radiotherapy too?
                  </summary>
                  <div className="text-gray-700 leading-relaxed pl-4">
                    <p className="mb-3">Yes. The digital version is suitable for:</p>
                    <ul className="space-y-2 mb-3">
                      <li className="flex items-start space-x-2">
                        <span className="text-accent-600">•</span>
                        <span>brachytherapy</span>
                      </li>
                      <li className="flex items-start space-x-2">
                        <span className="text-accent-600">•</span>
                        <span>pelvic radiotherapy</span>
                      </li>
                      <li className="flex items-start space-x-2">
                        <span className="text-accent-600">•</span>
                        <span>breast radiotherapy</span>
                      </li>
                      <li className="flex items-start space-x-2">
                        <span className="text-accent-600">•</span>
                        <span>abdominal or thoracic radiotherapy</span>
                      </li>
                      <li className="flex items-start space-x-2">
                        <span className="text-accent-600">•</span>
                        <span>MRI and CT scan anxiety</span>
                      </li>
                      <li className="flex items-start space-x-2">
                        <span className="text-accent-600">•</span>
                        <span>general anticipatory anxiety</span>
                      </li>
                    </ul>
                    <p>The Safe Place, breathwork, Anchor, and visualisation techniques are universal.</p>
                  </div>
                </details>

                <details className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-2xl p-6 group">
                  <summary className="font-semibold text-gray-900 cursor-pointer text-lg mb-2">
                    Can this help with MRI anxiety too?
                  </summary>
                  <p className="text-gray-700 leading-relaxed pl-4">
                    Yes. The same tools — Safe Place, breath regulation, visualisation and the Anchor — are extremely effective for MRI anxiety, CT scans, and other medical procedures.
                  </p>
                </details>

                <details className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-2xl p-6 group">
                  <summary className="font-semibold text-gray-900 cursor-pointer text-lg mb-2">
                    Do I need a referral?
                  </summary>
                  <p className="text-gray-700 leading-relaxed pl-4">
                    No. You can self-refer for both the in-person sessions and the digital programme.
                  </p>
                </details>

                <details className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-2xl p-6 group">
                  <summary className="font-semibold text-gray-900 cursor-pointer text-lg mb-2">
                    Is the digital programme as effective as in-person?
                  </summary>
                  <p className="text-gray-700 leading-relaxed pl-4">
                    It's different, but incredibly effective for many people. You receive the same neuroscience explanations and guided tools. Those who prefer learning privately or revisiting the techniques repeatedly often find digital especially helpful.
                  </p>
                </details>
              </div>
            </div>
          </div>
        </section>
        {/* Pillar link */}
        <section className="py-8 bg-white">
          <div className="container-custom max-w-4xl text-center">
            <a href="/medical-anxiety-hypnotherapy" className="text-accent-600 hover:text-accent-700 font-semibold inline-flex items-center gap-1">
              Learn more about medical procedure anxiety <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </section>
      </main>
    </>
  );
};

export default Radiotherapy;
